// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { actionListRenderer, ActionListRendererParams } from '@Helpers/actionListRenderer';
import { IsFlag } from '@Organisms/IsFlag/IsFlag';
import { ResponderDrawer } from '@Organisms/Responder';
import {
    CaseOutput,
    CaseStatusStage,
    CustomStatusMapType,
    CustomStatusOutput,
    PermissionsEnum,
    UserOutput,
} from 'thehive-sdk';
import { CaseBulkDelete } from './CaseBulkDelete/CaseBulkDelete';
import CaseBulkEditManage from './CaseBulkEdit/CaseBulkEditManage';
import { CaseBulkReopen } from './CaseBulkReopen/CaseBulkReopen';
import { CaseBulkStatusManage } from './CaseBulkStatusManage/CaseBulkStatusManage';
import { UseLocalMessageType } from '@Helpers/locale';
import { CaseEventList } from './eventList';
import CaseBulkCaseTemplateManage from './CaseBulkCaseTemplateManage/CaseBulkCaseTemplateManage';
import { PermissionExtract } from '@Hooks/useSplitPermissions';

interface CaseActionListProps {
    local: UseLocalMessageType;
    caseStatus: CustomStatusMapType;
    eventList: CaseEventList;
    customStatusList: CustomStatusOutput[];
    organisation: string;
    onDeleteBulk?: () => any;
    onEditBulkReopen?: () => any;
    onCloseBulkStatusManage?: () => any;
    onStartPoolStatusManage?: () => any;
    onFinishPoolStatusManage?: () => any;
    hasPermissions: Record<
        PermissionExtract<
            [
                PermissionsEnum.MANAGE_CASE_UPDATE,
                PermissionsEnum.MANAGE_CASE_DELETE,
                PermissionsEnum.MANAGE_ACTION,
                PermissionsEnum.MANAGE_CASE_REOPEN,
            ]
        >,
        boolean
    >;
    currentUserData?: UserOutput;
}

export const actionList = ({
    local,
    caseStatus,
    eventList,
    customStatusList,
    organisation,
    onCloseBulkStatusManage,
    onEditBulkReopen,
    onDeleteBulk,
    onStartPoolStatusManage,
    onFinishPoolStatusManage,
    hasPermissions,
    currentUserData,
}: CaseActionListProps): ActionListRendererParams<CaseOutput> =>
    actionListRenderer<CaseOutput>((selectedItems) => ({
        assigntome: {
            label: local('common.assigntome'),
            icon: <ICONS.UserPlus />,
            hidden: !(
                hasPermissions.updateCase && selectedItems?.some(({ assignee }) => assignee !== currentUserData?.login)
            ),
            scope: 'both',
            query: () => {
                eventList.onMultipleEdit(
                    { assignee: currentUserData?.login },
                    selectedItems.map(({ _id }) => _id),
                );
            },
        },
        edit: {
            label: local('case.actions.edit.label'),
            icon: <ICONS.EditIcon />,
            scope: 'list',
            hidden: !hasPermissions.updateCase,
            CustomComponent: (triggerElem): ReactElement => {
                const changeStatus = selectedItems.every(({ stage }) => stage !== CaseStatusStage.CLOSED);
                return (
                    <CaseBulkEditManage
                        triggerElem={triggerElem}
                        changeStatus={changeStatus}
                        caseStatus={caseStatus}
                        onEdit={(values) => {
                            if (Object.keys(values).length) {
                                return eventList.onBulkEdit(
                                    values,
                                    selectedItems.map(({ _id }) => _id),
                                );
                            }
                        }}
                    />
                );
            },
        },
        bulkCaseTemplate: {
            label: local('case.actions.bulk-caseTemplate.label'),
            icon: <ICONS.CaseTemplateIcon />,
            scope: 'both',
            hidden: !hasPermissions.updateCase,
            CustomComponent: (triggerElem): ReactElement => {
                return (
                    <CaseBulkCaseTemplateManage
                        triggerElem={triggerElem}
                        organisation={organisation}
                        onEdit={(values) => {
                            eventList.onBulkCaseTemplate(
                                values,
                                selectedItems.map(({ _id }) => _id),
                            );
                        }}
                    />
                );
            },
        },
        flag: {
            query: (): void => {
                eventList.onMultipleEdit(
                    { flag: true },
                    selectedItems.map(({ _id }) => _id),
                );
            },
            label: local('case.actions.flag.label'),
            hidden: selectedItems.some(({ flag }) => flag) || !hasPermissions.updateCase,
            scope: 'both',
            icon: <IsFlag value={true} />,
        },
        unflag: {
            query: (): void => {
                eventList.onMultipleEdit(
                    { flag: false },
                    selectedItems.map(({ _id }) => _id),
                );
            },
            label: local('case.actions.unflag.label'),
            hidden: selectedItems.some(({ flag }) => !flag) || !hasPermissions.updateCase,
            scope: 'both',
            icon: <IsFlag value={false} />,
        },
        close: {
            label: local('case.actions.close.label'),
            hidden: selectedItems.some(({ stage }) => stage === CaseStatusStage.CLOSED) || !hasPermissions.updateCase,
            scope: 'both',
            icon: <ICONS.CloseIcon />,
            CustomComponent: (triggerElem): ReactElement => (
                <CaseBulkStatusManage
                    triggerElem={triggerElem}
                    poolData={selectedItems}
                    statusList={customStatusList}
                    onClose={() => {
                        onCloseBulkStatusManage?.();
                    }}
                    onStart={onStartPoolStatusManage}
                    onFinish={onFinishPoolStatusManage}
                    onEdit={eventList.onEdit}
                />
            ),
        },
        reopen: {
            label: local('case.actions.reopen.label'),
            disabled: !hasPermissions.reopenCase,
            hidden: selectedItems.some(({ stage }) => stage !== CaseStatusStage.CLOSED),
            scope: 'both',
            icon: <ICONS.StartIcon />,
            CustomComponent: (triggerElem): ReactElement => (
                <CaseBulkReopen
                    triggerElem={triggerElem}
                    onMultipleEdit={(data) => {
                        eventList.onMultipleEdit(
                            data,
                            selectedItems.map(({ _id }) => _id),
                        );
                        onEditBulkReopen?.();
                    }}
                    countItems={selectedItems.length}
                    statusList={customStatusList}
                />
            ),
        },
        delete: {
            label: local('case.actions.delete.label'),
            icon: <ICONS.DeleteIcon />,
            scope: 'list',
            hidden: !selectedItems.every(({ extraData }) => extraData?.isOwner) || !hasPermissions.deleteCase,
            CustomComponent: (triggerElem): ReactElement => (
                <CaseBulkDelete
                    selectedCases={selectedItems}
                    triggerElem={triggerElem}
                    onMultipleDelete={(casesIds): void => {
                        eventList.onMultipleDelete(casesIds).then(() => {
                            onDeleteBulk?.();
                        });
                    }}
                />
            ),
        },
        responders: {
            label: local('case.actions.responders.label'),
            icon: <ICONS.RunResponderIcon />,
            scope: 'single',
            CustomComponent: (triggerElem): ReactElement => (
                <ResponderDrawer
                    objectId={selectedItems.find(Boolean)?._id || ''}
                    objectType="case"
                    triggerElem={triggerElem}
                    objectData={{ caseNumber: selectedItems.find(Boolean)?._id || '' }}
                />
            ),
        },
    }));
